import { doc, getDoc, onSnapshot, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { UserSettings, UserTutorials } from '@/types';

export const DEFAULT_SETTINGS: UserSettings = {
  notificationsEnabled: false,
  locationAllowed: false,
  analyticsConsent: false,
};

function settingsDocRef(uid: string) {
  return doc(db, 'users', uid, 'settings', 'main');
}

function tutorialsDocRef(uid: string) {
  return doc(db, 'users', uid, 'settings', 'tutorials');
}

function omitUndefined<T extends Record<string, unknown>>(data: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(data).filter(([, value]) => value !== undefined)
  ) as Partial<T>;
}

export const settingsService = {
  async getSettings(uid: string): Promise<UserSettings | null> {
    const snap = await getDoc(settingsDocRef(uid));
    if (!snap.exists()) return null;
    return { ...DEFAULT_SETTINGS, ...(snap.data() as Partial<UserSettings>) };
  },

  subscribeToSettings(uid: string, callback: (settings: UserSettings | null) => void) {
    return onSnapshot(settingsDocRef(uid), snap => {
      callback(
        snap.exists() ? { ...DEFAULT_SETTINGS, ...(snap.data() as Partial<UserSettings>) } : null
      );
    });
  },

  async updateSettings(uid: string, updates: Partial<UserSettings>): Promise<void> {
    const ref = settingsDocRef(uid);
    const now = new Date().toISOString();
    const clean = omitUndefined(updates);
    const snap = await getDoc(ref);

    if (snap.exists()) {
      await updateDoc(ref, {
        ...clean,
        updatedAt: now,
      });
    } else {
      await setDoc(ref, {
        ...DEFAULT_SETTINGS,
        ...clean,
        createdAt: now,
        updatedAt: now,
      });
    }
  },

  async getTutorials(uid: string): Promise<UserTutorials | null> {
    const snap = await getDoc(tutorialsDocRef(uid));
    return snap.exists() ? (snap.data() as UserTutorials) : null;
  },

  subscribeToTutorials(uid: string, callback: (tutorials: UserTutorials | null) => void) {
    return onSnapshot(tutorialsDocRef(uid), snap => {
      callback(snap.exists() ? (snap.data() as UserTutorials) : null);
    });
  },

  async updateTutorials(uid: string, updates: Partial<UserTutorials>): Promise<void> {
    await setDoc(tutorialsDocRef(uid), omitUndefined(updates), { merge: true });
  },
};
